<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { VueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';

import AgentLayout from 'dashboard/components-next/aiAgents/agent/AgentLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useBrain } from '../useBrain';
import AgentFlowNode from './canvas/AgentFlowNode.vue';
import AgentFlowPanel from './panels/AgentFlowPanel.vue';
import {
  NODE_TYPES,
  defaultNodeData,
  nextNodeId,
  deserializeGraph,
  lintGraph,
  buildDraftConfig,
} from './useAgentFlowGraph';

import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

const { t } = useI18n();
const store = useStore();
const { brain, brainId, fetchBrain } = useBrain();

const uiFlags = useMapGetter('aiAgentBrains/getUIFlags');
const allTools = useMapGetter('aiAgentTools/getRecords');
const allBrains = useMapGetter('aiAgentBrains/getRecords');
const isSaving = computed(() => uiFlags.value.updatingItem);

const nodes = ref([]);
const edges = ref([]);
const selectedNodeId = ref(null);
const selectedEdgeId = ref(null);
const hasConflict = ref(false);
const isHydrated = ref(false);

const toolNames = computed(
  () => new Set((allTools.value || []).map(tool => tool.name))
);
const otherBrains = computed(() =>
  (allBrains.value || [])
    .filter(b => b.id !== brain.value.id)
    .map(b => ({ id: b.id, display_name: b.display_name }))
);

const selectedNode = computed(
  () => nodes.value.find(n => n.id === selectedNodeId.value) || null
);
const selectedEdge = computed(
  () => edges.value.find(e => e.id === selectedEdgeId.value) || null
);
const lintIssues = computed(() =>
  lintGraph(nodes.value, edges.value, { toolNames: toolNames.value })
);

const hydrate = () => {
  const graph = deserializeGraph(brain.value.draft_config?.workflow);
  nodes.value = graph.nodes;
  edges.value = graph.edges;
  isHydrated.value = true;
};

watch(
  () => brain.value.id,
  id => {
    if (id && !isHydrated.value) hydrate();
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('aiAgentTools/get');
  store.dispatch('aiAgentBrains/get');
});

const addNode = type => {
  const id =
    type === 'start' && !nodes.value.some(n => n.type === 'start')
      ? 'start_node'
      : nextNodeId(nodes.value);
  nodes.value.push({
    id,
    type,
    position: {
      x: 80 + nodes.value.length * 220,
      y: 80 + nodes.value.length * 40,
    },
    data: defaultNodeData(type),
  });
  selectedEdgeId.value = null;
  selectedNodeId.value = id;
};

const onConnect = connection => {
  const exists = edges.value.some(
    e =>
      e.source === connection.source &&
      (e.sourceHandle || null) === (connection.sourceHandle || null) &&
      e.target === connection.target
  );
  if (exists) return;
  edges.value.push({
    id: `e-${connection.source}-${connection.sourceHandle || 'default'}-${connection.target}-${edges.value.length}`,
    source: connection.source,
    target: connection.target,
    sourceHandle: connection.sourceHandle || null,
    type: 'unconditional',
    condition: '',
    expr: '',
  });
};

const onNodeClick = ({ node }) => {
  selectedEdgeId.value = null;
  selectedNodeId.value = node.id;
};
const onEdgeClick = ({ edge }) => {
  selectedNodeId.value = null;
  selectedEdgeId.value = edge.id;
};
const onPaneClick = () => {
  selectedNodeId.value = null;
  selectedEdgeId.value = null;
};

const updateNode = updated => {
  nodes.value = nodes.value.map(n => (n.id === updated.id ? updated : n));
};
const updateEdge = updated => {
  edges.value = edges.value.map(e => (e.id === updated.id ? updated : e));
};
const deleteNode = id => {
  nodes.value = nodes.value.filter(n => n.id !== id);
  edges.value = edges.value.filter(e => e.source !== id && e.target !== id);
  selectedNodeId.value = null;
};
const deleteEdge = id => {
  edges.value = edges.value.filter(e => e.id !== id);
  selectedEdgeId.value = null;
};

const save = async () => {
  try {
    await store.dispatch('aiAgentBrains/update', {
      id: brain.value.id,
      draftConfig: buildDraftConfig(
        brain.value.draft_config,
        nodes.value,
        edges.value
      ),
      lockVersion: brain.value.lock_version,
    });
    hasConflict.value = false;
    useAlert(t('AI_AGENTS.FLOW_EDITOR.SAVED'));
  } catch (error) {
    if (error.response?.status === 409) hasConflict.value = true;
    else useAlert(t('AI_AGENTS.AGENT.PUBLISH.ERROR'));
  }
};

const reloadDraft = async () => {
  await fetchBrain();
  hydrate();
  hasConflict.value = false;
};

watch(brainId, () => {
  isHydrated.value = false;
  if (brain.value.id) hydrate();
});
</script>

<template>
  <AgentLayout v-if="brain.id" :brain="brain">
    <div class="flex flex-col gap-3 h-[70vh]">
      <div class="flex items-start justify-between gap-3">
        <div>
          <h2 class="text-lg font-medium text-n-slate-12">
            {{ t('AI_AGENTS.FLOW_EDITOR.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('AI_AGENTS.FLOW_EDITOR.DESCRIPTION') }}
          </p>
        </div>
        <Button
          data-testid="save-flow"
          :label="t('AI_AGENTS.FLOW_EDITOR.SAVE')"
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="save"
        />
      </div>

      <div
        v-if="hasConflict"
        class="flex items-center justify-between gap-3 px-3 py-2 text-sm rounded-lg bg-n-amber-3 text-n-amber-12"
      >
        {{ t('AI_AGENTS.AGENT.CONFLICT.MESSAGE') }}
        <Button
          :label="t('AI_AGENTS.AGENT.CONFLICT.RELOAD')"
          color="amber"
          size="sm"
          @click="reloadDraft"
        />
      </div>

      <div
        v-if="lintIssues.length"
        class="flex flex-wrap gap-2 rounded-lg bg-n-amber-2 px-3 py-2 text-xs text-n-slate-12"
      >
        <span v-for="issue in lintIssues" :key="issue">
          {{ t(`AI_AGENTS.FLOW_EDITOR.LINT.${issue}`) }}
        </span>
      </div>

      <div
        class="flex flex-1 min-h-0 rounded-lg border border-n-weak overflow-hidden"
      >
        <aside
          class="flex w-40 flex-col gap-1 border-r border-n-weak p-2 overflow-y-auto"
        >
          <span class="text-xs font-semibold uppercase text-n-slate-11">
            {{ t('AI_AGENTS.FLOW_EDITOR.PALETTE') }}
          </span>
          <Button
            v-for="type in NODE_TYPES"
            :key="type"
            color="slate"
            variant="faded"
            size="sm"
            :label="t(`AI_AGENTS.FLOW_EDITOR.NODE.${type.toUpperCase()}`)"
            @click="addNode(type)"
          />
        </aside>

        <main class="flex-1 min-w-0">
          <VueFlow
            v-model:nodes="nodes"
            v-model:edges="edges"
            fit-view-on-init
            :max-zoom="1.5"
            :min-zoom="0.25"
            @connect="onConnect"
            @node-click="onNodeClick"
            @edge-click="onEdgeClick"
            @pane-click="onPaneClick"
          >
            <Background />
            <Controls />
            <template #node-start="nodeProps">
              <AgentFlowNode v-bind="nodeProps" />
            </template>
            <template #node-end="nodeProps">
              <AgentFlowNode v-bind="nodeProps" />
            </template>
            <template #node-override_agent="nodeProps">
              <AgentFlowNode v-bind="nodeProps" />
            </template>
            <template #node-dispatch_tool="nodeProps">
              <AgentFlowNode v-bind="nodeProps" />
            </template>
            <template #node-agent_transfer="nodeProps">
              <AgentFlowNode v-bind="nodeProps" />
            </template>
            <template #node-transfer_to_number="nodeProps">
              <AgentFlowNode v-bind="nodeProps" />
            </template>
            <template #node-language_detection="nodeProps">
              <AgentFlowNode v-bind="nodeProps" />
            </template>
            <template #node-voicemail_detection="nodeProps">
              <AgentFlowNode v-bind="nodeProps" />
            </template>
            <template #node-play_keypad_touch_tone="nodeProps">
              <AgentFlowNode v-bind="nodeProps" />
            </template>
            <template #node-end_call="nodeProps">
              <AgentFlowNode v-bind="nodeProps" />
            </template>
          </VueFlow>
        </main>

        <AgentFlowPanel
          v-if="selectedNode || selectedEdge"
          :node="selectedNode"
          :edge="selectedEdge"
          :tools="allTools || []"
          :brains="otherBrains"
          @update:node="updateNode"
          @update:edge="updateEdge"
          @delete-node="deleteNode"
          @delete-edge="deleteEdge"
        />
      </div>
    </div>
  </AgentLayout>
  <div
    v-else
    class="flex items-center justify-center w-full h-full text-n-slate-11"
  >
    <Spinner />
  </div>
</template>

<style>
.vue-flow__handle {
  width: 12px;
  height: 12px;
  border-width: 2px;
}
.vue-flow__handle:hover {
  width: 16px;
  height: 16px;
}
</style>
